"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { HotspotDot } from "@/components/ui/hotspot-dot";

export interface DemoOpener {
  /** Id do passo que este elemento abre (`data-demo-open`). */
  readonly stepId: string;
  /** Rótulo acessível da bolinha. */
  readonly label: string;
}

export interface DemoHotspotLayerProps {
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly visible: boolean;
  readonly onSelect: (opener: DemoOpener) => void;
}

interface PlacedHotspot {
  readonly opener: DemoOpener;
  readonly left: number;
  readonly top: number;
}

/**
 * Uma bolinha por alvo do roteiro da tela.
 *
 * Vai em portal para o `body`, como o callout: dentro do palco ela seria
 * esticada pela câmera (o zoom é `transform: scale`) e cortada pelo
 * `overflow-hidden` do aparelho. Em portal, a posição é recalculada — e alvo
 * que saiu da área visível da tela simplesmente não desenha bolinha.
 */
export function DemoHotspotLayer({
  containerRef,
  visible,
  onSelect,
}: DemoHotspotLayerProps) {
  const [placed, setPlaced] = useState<PlacedHotspot[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const sync = useCallback(() => {
    const container = containerRef.current;
    if (!container || !visible) {
      setPlaced((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const screenEl =
      container.closest<HTMLElement>("[data-demo-screen]") ?? container;
    const bounds = screenEl.getBoundingClientRect();

    /*
     * A fonte são os elementos da tela, não o roteiro: quem pode ser clicado
     * declara `data-demo-open` com o passo que abre. Assim a bolinha nasce onde
     * a pessoa clicaria no app de verdade — e o que está dentro de um modal só
     * aparece quando o modal está aberto, porque aí o elemento existe.
     *
     * Com um modal aberto a busca se fecha nele: o app atrás continua no DOM,
     * e sem isto as bolinhas dele ficam flutuando por cima do painel, em cima
     * de coisa que aquele clique não alcança.
     */
    // A busca parte da tela, não do container: o modal é portado para a
    // superfície, como irmão do app replicado — de dentro do container ele é
    // invisível.
    const escopo =
      screenEl.querySelector<HTMLElement>("[data-demo-overlay]") ?? container;

    const next = [
      ...escopo.querySelectorAll<HTMLElement>("[data-demo-open]"),
    ].flatMap((element) => {
      const stepId = element.dataset.demoOpen;
      if (!stepId) return [];

      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return [];

      const left = Math.round(rect.left + rect.width / 2);
      const top = Math.round(rect.top + Math.min(rect.height / 2, 28));

      const insideScreen =
        left > bounds.left + 4 &&
        left < bounds.right - 4 &&
        top > bounds.top + 4 &&
        top < bounds.bottom - 4;

      if (!insideScreen) return [];

      const label =
        element.dataset.demoOpenLabel ??
        element.getAttribute("aria-label") ??
        element.textContent?.trim().slice(0, 80) ??
        "Ver este ponto";

      return [{ opener: { stepId, label }, left, top }];
    });

    // Medir a cada quadro só vale se o resultado mudar: sem esta comparação, um
    // estado novo por quadro re-renderizaria a página inteira sem nada ter se
    // movido. As coordenadas já vêm arredondadas, então a comparação é exata.
    setPlaced((prev) =>
      prev.length === next.length &&
      prev.every(
        (item, index) =>
          item.opener.stepId === next[index].opener.stepId &&
          item.left === next[index].left &&
          item.top === next[index].top,
      )
        ? prev
        : next,
    );
  }, [containerRef, visible]);

  useEffect(() => {
    sync();

    if (!visible) return;

    const scrollRoot = containerRef.current?.querySelector<HTMLElement>(
      "[data-demo-scroll-root]",
    );

    /*
     * A cada quadro, e não a cada 250ms: a câmera do palco se move por
     * `transform` durante ~680ms, e num intervalo a bolinha andava em degraus
     * atrás do alvo. O `sync` só troca o estado quando alguma coordenada muda,
     * então o laço não custa render.
     */
    let frame = 0;
    const tick = () => {
      /*
       * O reagendamento vem antes da medição de propósito: se `sync` lançar
       * num quadro (alvo que sumiu no meio da troca de tela, por exemplo), o
       * laço continua vivo. Com a ordem invertida, uma exceção derrubava a
       * camada inteira, em silêncio, até a próxima montagem.
       */
      frame = window.requestAnimationFrame(tick);
      sync();
    };
    frame = window.requestAnimationFrame(tick);

    /*
     * Rede de segurança, e não redundância: o navegador **para** o
     * `requestAnimationFrame` em aba oculta, e aí a camada nunca mediria —
     * nenhuma bolinha aparecia ao voltar para a aba. O temporizador continua
     * correndo (devagar) mesmo oculto, e como o `sync` só troca o estado
     * quando alguma coordenada muda, ele não custa render nenhum.
     */
    const backstop = window.setInterval(sync, 400);

    /*
     * Voltar para a aba precisa de uma medição imediata: em aba oculta o
     * Chrome estrangula o temporizador (uma vez por minuto, depois de cinco
     * minutos escondida) e congela o `requestAnimationFrame`. Sem isto, quem
     * volta para a demo fica até um minuto sem bolinha nenhuma.
     */
    const aoVoltarParaAba = () => {
      if (!document.hidden) sync();
    };

    scrollRoot?.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    document.addEventListener("visibilitychange", aoVoltarParaAba);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearInterval(backstop);
      scrollRoot?.removeEventListener("scroll", sync);
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      document.removeEventListener("visibilitychange", aoVoltarParaAba);
    };
  }, [sync, containerRef, visible]);

  if (!mounted || !visible || placed.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[99998]">
      {placed.map(({ opener, left, top }) => (
        <div
          key={`${opener.stepId}-${left}-${top}`}
          className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left, top }}
        >
          <HotspotDot label={opener.label} onClick={() => onSelect(opener)} />
        </div>
      ))}
    </div>,
    document.body,
  );
}
